import {
  useArchiveConversation,
  useUnarchiveConversation,
} from 'Modules/conversation/conversations-list/requests/index.requests';
import { useCallback } from 'react';
import { UseConversationArchiveProps } from './index.types';

export const useConversationArchive = ({
  conversationId,
  doWhenOpen,
  doWhenConfirm,
  doWhenFinished,
  isArchived = false,
}: UseConversationArchiveProps) => {
  const { mutate: archiveConversation } = useArchiveConversation();
  const { mutate: unarchiveConversation } = useUnarchiveConversation();

  const handleArchiveConversation = useCallback(
    (conversationId: string) => {
      try {
        archiveConversation({ conversationId, source: 'chat_list' });
      } catch (error) {
        console.error('Failed to archive conversation:', error);
      } finally {
        doWhenFinished?.();
      }
    },
    [archiveConversation, doWhenFinished]
  );

  const handleUnarchiveConversation = useCallback(
    (
      conversationId: string,
      source: 'chat_list' | 'conversation' | 'conversation_detail' = 'chat_list'
    ) => {
      try {
        unarchiveConversation({ conversationId, source });
      } catch (error) {
        console.error('Failed to unarchive conversation:', error);
      } finally {
        doWhenFinished?.();
      }
    },
    [unarchiveConversation, doWhenFinished]
  );

  const openArchiveConversation = useCallback(() => {
    if (!conversationId) return;

    doWhenOpen?.();
    doWhenConfirm?.();

    if (isArchived) {
      void handleUnarchiveConversation(conversationId);
    } else {
      void handleArchiveConversation(conversationId);
    }
  }, [
    conversationId,
    doWhenOpen,
    doWhenConfirm,
    isArchived,
    handleArchiveConversation,
    handleUnarchiveConversation,
  ]);

  return {
    openArchiveConversation,
    handleUnarchiveConversation,
  };
};
